import { AlertCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  isDestructive?: boolean;
  isPending?: boolean;
  /** Error message rendered inside the dialog body (e.g. server-side
   *  rejection from the action). The dialog stays open so the user can
   *  read the reason and either cancel or correct. */
  error?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  isDestructive,
  isPending,
  error,
}: ConfirmDialogProps) {
  const Icon = isDestructive ? AlertTriangle : Info;
  const eyebrowLabel = isDestructive ? 'Confirm action' : 'Review';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0" aria-live="assertive">
        <div className="flex flex-col gap-2 px-7 pt-7 pr-12">
          <div
            className={cn(
              'inline-flex items-center gap-2',
              isDestructive ? 'text-error-700' : 'text-primary-600',
            )}
          >
            <Icon className="size-3.5" strokeWidth={1.75} aria-hidden />
            <span className="text-meta-sm tracking-eyebrow font-medium uppercase">
              {eyebrowLabel}
            </span>
          </div>
          <DialogTitle className="font-display text-accent-ink mt-1 text-[1.375rem] leading-tight font-medium">
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription className="text-body-sm mt-2 leading-relaxed text-neutral-600">
              {description}
            </DialogDescription>
          ) : null}
          {error ? (
            <div
              role="alert"
              className="border-error-300 bg-error-50 text-error-700 mt-3 flex items-start gap-2 rounded-md border px-3 py-2"
            >
              <AlertCircle
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
                strokeWidth={2}
                aria-hidden="true"
              />
              <p className="text-body-sm leading-snug">{error}</p>
            </div>
          ) : null}
        </div>

        <div className="mt-7 flex items-center justify-end gap-2 border-t border-(--color-border) bg-neutral-50/60 px-5 py-3.5">
          <Button
            variant="ghost"
            size="md"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isPending}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={isDestructive ? 'destructive' : 'primary'}
            size="md"
            onClick={() => {
              void onConfirm();
            }}
            disabled={isPending}
            className="min-w-24"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                {confirmLabel}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
